// src/pages/User/components/ResultsDashboard.jsx
import React from 'react';
import QuizEditor from "./QuizEditor";

const summaryHtmlClass =
  "text-gray-700 leading-relaxed text-[15px] md:text-[16px] " +
  "max-h-[70vh] overflow-y-auto pr-4 custom-scroll " +
  "tracking-[0.001em] " +
  "[&_h1]:text-2xl md:[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mb-3 [&_h1]:mt-5 " +
  "[&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[#1E40AF] [&_h2]:mb-3 [&_h2]:mt-5 " +
  "[&_h3]:text-xl md:[&_h3]:text-2xl [&_h3]:font-bold [&_h3]:text-[#1E40AF] [&_h3]:mb-3 [&_h3]:mt-6 " +
  "[&_div]:mb-4 [&_p]:mb-3 " +
  "[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 " +
  "[&_li]:leading-relaxed " +
  "[&_b]:font-bold [&_b]:text-gray-800 [&_strong]:font-bold [&_strong]:text-gray-800";

const ResultsDashboard = ({
  data,
  config,
  onBack,
  onSave,
  onDownload,
  onUpdateQuiz,
  originalContent = "",
  isSaving,
  isSaveDisabled,
}) => {
  return (
    <div className="animate-fadeIn pb-20">
      {/* Header / Actions */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <button 
          onClick={onBack} 
          className="text-gray-500 hover:text-gray-800 font-bold self-start md:self-center flex items-center gap-2"
        >
          <i className="fa-solid fa-arrow-left"></i> Create New
        </button>
        
        <div className="flex gap-3 w-full md:w-auto">
          <button 
            onClick={onSave} 
            disabled={isSaveDisabled}
            className={`flex-1 md:flex-none px-6 py-2.5 rounded-xl font-bold shadow-lg transition-all flex items-center justify-center gap-2 ${
              isSaveDisabled
                ? "bg-indigo-300 text-white cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-700 hover:-translate-y-1"
            }`}
          >
            <i className={`fa-solid ${isSaveDisabled ? "fa-check" : "fa-cloud-arrow-up"}`}></i>
            {isSaving ? "Saving..." : isSaveDisabled ? "Saved to Dashboard" : "Save to Dashboard"}
          </button>
          <button 
            onClick={onDownload} 
            className="flex-1 md:flex-none bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold shadow-lg hover:bg-blue-700 hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
          >
            <i className="fa-solid fa-download"></i> Download PDF
          </button>
        </div>
      </div>
      
      {/* 1. Summary Section */}
      {data.summary && config.summaryEnabled && (
        <div className="bg-white p-8 rounded-2xl shadow-sm mb-8 border border-gray-100 flex flex-col">
          <div className="flex items-center gap-3 mb-4 border-b pb-4 shrink-0">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xl">
              <i className="fa-solid fa-align-left"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Smart Summary</h2>
          </div>
          <div 
            className={summaryHtmlClass}
            dangerouslySetInnerHTML={{ __html: data.summary }}
          ></div>
        </div>
      )}

      {/* 2. Flashcards Section */}
      {data.flashcards && data.flashcards.length > 0 && config.flashcardsEnabled && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center text-xl">
              <i className="fa-solid fa-layer-group"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Flashcards ({data.flashcards.length})</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.flashcards.map((card, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition flex flex-col h-full group">
                <div className="mb-4">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Concept</div>
                  <div className="font-bold text-gray-800 text-lg group-hover:text-blue-600 transition-colors">{card.front}</div>
                </div>
                <div className="mt-auto pt-4 border-t border-gray-50">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">Answer</div>
                  <div className="text-gray-600">{card.back}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. Quiz Section with Full Interactive Editor */}
      {data.quiz && data.quiz.length > 0 && config.quizEnabled && (
        <div className="mb-8">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl text-blue-800 text-center text-sm font-medium mb-6">
            <i className="fa-solid fa-circle-info mr-2"></i> 
            {data.quiz.length} Quiz questions prepared. (Timer: {config.timePerQuestion}s / question)
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-gray-100">
            <QuizEditor
              questions={data.quiz}
              config={config}
              onUpdateQuiz={onUpdateQuiz}
              originalContent={originalContent}
              regenLimit={3}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsDashboard;
